import { formatTime } from '../utils/formatDate';
import React, { createContext, useContext, useState, useCallback } from 'react';

export interface AuditEntry {
  id: string;
  action: string;
  timestamp: string;
  user: string;
  page?: string;
  entity?: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
}

interface AuditLogContextType {
  entries: AuditEntry[];
  addEntry: (entry: Omit<AuditEntry, 'id' | 'timestamp'>) => void;
  clearEntries: () => void;
}

const AuditLogContext = createContext<AuditLogContextType | undefined>(undefined);

export const AuditLogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);

  const addEntry = useCallback((entry: Omit<AuditEntry, 'id' | 'timestamp'>) => {
    const newEntry: AuditEntry = {
      ...entry,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: formatTime(new Date()),
    };
    setEntries(prev => [newEntry, ...prev].slice(0, 100)); // Keep last 100 entries
  }, []);

  const clearEntries = useCallback(() => {
    setEntries([]);
  }, []);

  return (
    <AuditLogContext.Provider value={{ entries, addEntry, clearEntries }}>
      {children}
    </AuditLogContext.Provider>
  );
};

export const useAuditLog = () => {
  const context = useContext(AuditLogContext);
  if (!context) {
    throw new Error('useAuditLog must be used within AuditLogProvider');
  }
  return context;
};
